/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectorRef, Component, Inject, OnInit} from '@angular/core';
import {Subject} from 'rxjs';
import {BsModalRef} from 'ngx-bootstrap/modal';
import * as _ from 'lodash';
import {TeamResource} from '../../components/api/resources/resource/team.resource';
import {SiteResource} from '../../components/api/resources/resource/site.resource';
import {deferMarkForCheck} from '../common/util/defer-mark-for-check';

@Component({
  standalone: false,
  selector: 'adn-site-access-modal',
  template: `
    <form class="form-horizontal" (ngSubmit)="saveAndClose()">
      <div class="modal-header dialogAddStuff">
        <button type="button" class="close" (click)="dismiss()">&times;</button>
        <h4 class="modal-title">Advertiser Access to Sites</h4>
      </div>
      <div class="modal-body form-horizontal">
        <simple-error-messages [errors]="validationErrors"></simple-error-messages>
        <div class="form-group">
          <label class="control-label col-md-2">Sites</label>
          <div class="col-md-10">
            <p class="form-control-static">
              <span *ngFor="let site of teamReport.sites; let last = last">{{ site.name }}<span *ngIf="!last">, </span></span>
            </p>
          </div>
        </div>
        <adn-promise-spinner [resolved]="allNotAxPublisherTeams">
          <div class="form-group">
            <label class="control-label col-md-2">{{ 'team.plural' | ogTranslate }}</label>
            <div class="col-md-10">
              <p class="alert alert-warning" *ngIf="teamsWarning">The site could not be added to all the teams you selected because you lack the correct permission for the teams.</p>
              <div *ngIf="allNotAxPublisherTeams?.length > 1" class="allTeamsSelector">
                <a (click)="selectTeams()">Select all teams</a> | <a (click)="deselectTeams()">Clear all teams</a>
              </div>
              <ul class="list-unstyled doubleColumned">
                <li class="checkbox" *ngFor="let team of allNotAxPublisherTeams; trackBy: trackByTeamId">
                  <label>
                    <input type="checkbox" [checked]="isTeamSelected(team.id)" (change)="toggleTeam(team.id, $event)">
                    {{ team.name }}
                    <div class="small help-block"><small>{{ 'team.type.' + team.type | ogTranslate }}</small></div>
                  </label>
                </li>
              </ul>
            </div>
          </div>
        </adn-promise-spinner>
      </div>
      <div class="modal-footer">
        <a class="btn btn-link btn-xs" (click)="dismiss()">{{ 'button.cancel' | ogTranslate }}</a>
        <button type="submit" class="btn btn-default">Update Access to Sites</button>
      </div>
    </form>
  `
})
export class SiteAccessModalComponent implements OnInit {
  teamReport: any;

  readonly onSaved = new Subject<void>();

  selTeams: string[] = [];
  allNotAxPublisherTeams: any[] | undefined;
  validationErrors: any[] = [];
  teamsWarning = false;

  constructor(
    public bsModalRef: BsModalRef,
    @Inject(TeamResource) private readonly Team: any,
    @Inject(SiteResource) private readonly Site: any,
    private readonly cdr: ChangeDetectorRef
  ) {
  }

  ngOnInit(): void {
    this.selTeams = _.map(this.teamReport?.teams || [], 'id');
    this.Team.query({minimal: true}).$promise.then((page: any) => {
      deferMarkForCheck(this.cdr, () => {
        this.allNotAxPublisherTeams = _.filter(page.results || [], (team: any) => {
          return team.type !== 'AX_PUBLISHER' && team.type !== 'CREATIVE_AGENCY';
        });
      });
    });
  }

  selectTeams(): void {
    this.selTeams = _.map(this.allNotAxPublisherTeams || [], 'id');
  }

  deselectTeams(): void {
    this.selTeams = [];
  }

  isTeamSelected(teamId: string): boolean {
    return (this.selTeams || []).indexOf(teamId) > -1;
  }

  toggleTeam(teamId: string, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    const teams = [...(this.selTeams || [])];
    const index = teams.indexOf(teamId);
    if (checked && index < 0) {
      teams.push(teamId);
    } else if (!checked && index > -1) {
      teams.splice(index, 1);
    }
    this.selTeams = teams;
  }

  trackByTeamId(_index: number, team: any): string {
    return team.id;
  }

  saveAndClose(): void {
    const objectsForUpdating = _.map(this.teamReport?.sites || [], (site: any) => {
      return {
        id: site.id,
        teams: this.selTeams
      };
    });
    this.Site.bulkSave(objectsForUpdating).$promise.then(() => {
      this.onSaved.next();
      this.bsModalRef.hide();
    }, (errors: any) => {
      deferMarkForCheck(this.cdr, () => {
        this.validationErrors = _.get(errors, ['data', 'errors'], []);
      });
    });
  }

  dismiss(): void {
    this.bsModalRef.hide();
  }
}
